// apps/client/src/ui/minimap.tsx — layout preview + plot picker (founding and invites)
import { t } from '@whiskerton/content';
import { minimapRGBA } from '@whiskerton/shared/map/minimap';   // subpath-only (not in the barrel)
import type { GeneratedTown } from '@whiskerton/shared';
import { useEffect, useMemo, useRef } from 'react';
import { minPlotSpacing, nearestPlot, pickablePlots, plotCentre, toMapPoint } from './plotPick';

/** Fresh layout seeds for founding: crypto.getRandomValues → 0 … 2^31 − 1. */
export function freshSeeds(n: number): number[] {
  return Array.from(crypto.getRandomValues(new Uint32Array(n)), (v) => v % 2 ** 31);
}

/** With `onPick`, the whole map is the touch target (production bug: plots drawn 6–15 px wide on a phone could not be hit): a tap
 *  anywhere picks the pickable plot nearest it. Each plot keeps a real button (its number, at least 24 px where the plots are far enough
 *  apart, never so big that neighbours cover each other's middles) for keyboards and screen readers. Plots that cannot be picked let taps
 *  through to the map. */
export function Minimap(p: { town: GeneratedTown; selected?: number | null; onPick?: (plot: number) => void;
  taken?: readonly { plot: number; kind: 'resident' | 'villager'; name: string }[]; free?: readonly number[] }): React.ReactElement {
  const canvas = useRef<HTMLCanvasElement>(null);
  const { map } = p.town;
  const pixels = useMemo(() => minimapRGBA(map, p.town.objects), [map, p.town.objects]);
  const pickable = useMemo(() => pickablePlots(map.plots, p.taken, p.free), [map.plots, p.taken, p.free]);
  const spacing = useMemo(() => minPlotSpacing(map.plots), [map.plots]);
  useEffect(() => {
    let ctx: CanvasRenderingContext2D | null = null;
    try { ctx = canvas.current?.getContext('2d') ?? null; } catch { /* no 2D canvas (tests) */ }
    if (!ctx || typeof ImageData === 'undefined') return;              // plots still render as buttons
    ctx.putImageData(new ImageData(new Uint8ClampedArray(pixels), map.w, map.h), 0, 0);
  }, [pixels, map.w, map.h]);
  const pct = (v: number, of: number) => `${(v / of) * 100}%`;
  const onPick = p.onPick;

  const onClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!onPick) return;
    const button = (e.target as Element).closest<HTMLButtonElement>('button[data-plot]');
    const own = button && !button.disabled ? Number(button.dataset.plot) : null;
    // A keyboard or screen-reader click (detail 0) means the focused plot; a tap or click means the plot nearest the point.
    const at = e.detail === 0 ? null : toMapPoint(e.clientX, e.clientY, e.currentTarget.getBoundingClientRect(), map);
    const plot = at ? nearestPlot(map.plots, pickable, at) : own;
    if (plot !== null) onPick(plot);
  };

  // Marker size: the plot's own size, grown toward 24 px but to no more than 1.2 × the closest two plots' spacing (so two markers may
  // touch, but neither covers the other's number). Percentages of the map's width, so it scales with the map.
  const marker = `max(${pct(map.plots[0]?.rect.w ?? 0, map.w)}, min(24px, ${pct(Math.min(spacing, map.w) * 1.2, map.w)}))`;
  return (
    <div className="minimap" data-plot-map={onPick ? '' : undefined} onClick={onPick ? onClick : undefined}
      style={{ position: 'relative', aspectRatio: `${map.w} / ${map.h}`, borderRadius: 14, overflow: 'hidden', boxShadow: 'var(--sh-soft)',
        cursor: onPick ? 'pointer' : undefined, touchAction: onPick ? 'manipulation' : undefined }}>
      <canvas ref={canvas} width={map.w} height={map.h} style={{ display: 'block', width: '100%', height: '100%', imageRendering: 'pixelated' }} />
      {onPick && map.plots.map((plot) => {
        const taken = p.taken?.find((x) => x.plot === plot.index);
        const canPick = pickable.includes(plot.index);
        const selected = p.selected === plot.index;
        const c = plotCentre(plot);
        return (
          <button key={plot.index} type="button" className="hit" disabled={!canPick} data-plot={plot.index}
            aria-label={taken ? t('towns.plotTaken', { name: taken.name }) : t('towns.plot', { n: plot.index + 1 })} aria-pressed={selected}
            style={{ position: 'absolute', left: pct(c.x, map.w), top: pct(c.y, map.h), width: marker, aspectRatio: '1', height: 'auto',
              transform: `translate(-50%, -50%)${selected ? ' scale(1.15)' : ''}`, zIndex: selected ? 2 : 1,
              display: 'grid', placeItems: 'center', boxSizing: 'border-box', border: 0, borderRadius: 999, padding: 0,
              font: 'inherit', fontSize: 11, fontWeight: 900, lineHeight: 1, cursor: 'pointer', pointerEvents: canPick ? undefined : 'none',
              color: selected || !canPick ? '#fff' : 'var(--wood-800)',
              background: selected ? 'var(--leaf-700)' : canPick ? 'rgba(255,251,241,.92)' : 'rgba(122,90,62,.72)',
              boxShadow: selected ? '0 0 0 2px var(--cream-50), 0 2px 4px rgba(23,52,29,.45)' : '0 1px 3px rgba(74,58,44,.35)' }}>
            {plot.index + 1}
          </button>
        );
      })}
    </div>
  );
}
